import React, { useEffect, useState } from 'react';
import { ArrowLeft, Download, Printer, Loader2, FileText, Truck, CheckSquare, MapPin, Edit3, Check, History, AlertTriangle, PackagePlus, ExternalLink, ArrowRightLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { orderConfirmationAPI } from '../../services/api';
import ApprovalStatusBadge from '../ui/ApprovalStatusBadge';
import { SalesDocumentTrail } from '../ui/SalesDocumentTrail';
import RevisionHistory from '../ui/RevisionHistory';
import { isSqmMode, sqmLineDisplay } from './signageSqmUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  Draft: 'bg-gray-100 text-gray-600',
  Confirmed: 'bg-green-100 text-green-700',
  'Ready to Dispatch': 'bg-amber-100 text-amber-700',
  'Partially Dispatched': 'bg-cyan-100 text-cyan-700',
  Dispatched: 'bg-blue-100 text-blue-700',
  Cancelled: 'bg-red-100 text-red-700',
};

const OCDetailView = ({
  oc,
  amendments,
  confirmLoading,
  canMarkDispatch,
  canAddInvoice,
  canAmend,
  canConfirmDispatch,
  confirmDispatchLoading,
  branches = [],
  onBack,
  onEditDraft,
  onConfirmDraft,
  onAmend,
  onDispatch,
  onInvoice,
  onConfirmDispatch,
  onDownload,
  onPrint,
}) => {
  const isSignage = oc.oc_type === 'signage' && oc.signage_items?.length > 0;
  const navigate = useNavigate();
  const canCreateDispatch = ['Ready to Dispatch', 'Dispatched', 'Partially Dispatched'].includes(oc.status);
  const hasInvoice = !!oc.tally_invoice_number;
  const showConfirmTick = canConfirmDispatch && hasInvoice &&
    ['Ready to Dispatch', 'Partially Dispatched'].includes(oc.status);

  // Stuck-dispatch reconciliation: two sub-patterns of the same legacy-bypass bug:
  //   Type A: OC.status='Dispatched' but linked dispatch stuck in 'Awaiting Invoice'
  //   Type B: OC.status='Dispatched' but ZERO dispatches were ever created
  // In both cases stock has NOT been deducted via the modern movement-balance engine.
  const stuckCount = Number(oc.stuck_awaiting_invoice_count || 0);
  const missingDispatch = !!oc.missing_dispatch;
  const reconcileKind = missingDispatch ? 'type_b' : (stuckCount > 0 ? 'type_a' : null);
  const needsReconcile = (stuckCount > 0 || missingDispatch) && canConfirmDispatch &&
    ['Dispatched', 'Partially Dispatched'].includes(oc.status);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);
  const [reconcileInvNumber, setReconcileInvNumber] = useState('');
  const [reconcileInvDate, setReconcileInvDate] = useState('');
  const [reconcileInvUrl, setReconcileInvUrl] = useState('');
  const [reconcileInvName, setReconcileInvName] = useState('');
  const [reconcileLoading, setReconcileLoading] = useState(false);

  const handleReconcile = async () => {
    if (!hasInvoice && !reconcileInvNumber.trim()) {
      toast.error('Invoice number is required'); return;
    }
    if (!hasInvoice && !reconcileInvUrl) {
      toast.error('Please upload the invoice document'); return;
    }
    try {
      setReconcileLoading(true);
      const payload = hasInvoice ? {} : {
        invoice_number: reconcileInvNumber.trim(),
        invoice_date: reconcileInvDate,
        invoice_document_url: reconcileInvUrl,
        invoice_document_name: reconcileInvName,
      };
      const r = await orderConfirmationAPI.reconcileDispatch(oc.id, payload);
      const acted = r.dispatches_created
        ? `${r.dispatches_created} dispatch(es) created`
        : `${r.dispatches_activated} dispatch(es) activated`;
      toast.success(`Reconciled — ${acted}, stock deducted.`);
      setShowReconcileDialog(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcileLoading(false);
    }
  };

  // Cross-branch banner: signage produced at Ranchi but OC ships from another branch
  const ranchiBranch = (branches || []).find(b => /ranchi.*hq/i.test(b.name || ''));
  const ocBranchName = oc.branch_name || (branches || []).find(b => b.id === oc.branch)?.name || '';

  // Edit Vehicle Number — only allowed before invoice is uploaded (dispatch is Awaiting Invoice)
  const _user = (() => { try { return JSON.parse(localStorage.getItem('prismoline_user') || '{}'); } catch { return {}; } })();
  const _isAdmin = _user.role === 'Admin' || _user.role === 'Manager';
  const canEditVehicle = _isAdmin && !hasInvoice && !!oc.latest_dispatch_number && oc.status === 'Ready to Dispatch';
  const [showEditVehicleDialog, setShowEditVehicleDialog] = useState(false);
  const [editVehicleValue, setEditVehicleValue] = useState('');
  const [savingVehicle, setSavingVehicle] = useState(false);

  // Admin one-off: swap branch on a dispatched OC (use case: OC-2026-0404 was
  // dispatched against the wrong branch). Endpoint flips the OC header branch +
  // updates linked dispatches' from_branch — stock balance recomputes automatically.
  const canSwapBranch = _user.role === 'Admin';
  const [showSwapBranchDialog, setShowSwapBranchDialog] = useState(false);
  const [swapBranchName, setSwapBranchName] = useState('');
  const [swapMirrorShip, setSwapMirrorShip] = useState(true);
  const [swapPreview, setSwapPreview] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const runSwapBranch = async (apply) => {
    if (!swapBranchName.trim()) { toast.error('Please enter the new branch name'); return; }
    setSwapLoading(true);
    try {
      const r = await orderConfirmationAPI.swapBranch(oc.id, {
        new_branch_name: swapBranchName.trim(),
        ship_from_mirrors_branch: swapMirrorShip,
        dry_run: !apply,
      });
      if (!apply) {
        setSwapPreview(r.proposed);
      } else {
        toast.success(`Branch swapped — ${r.summary?.dispatches_to_update?.length || 0} dispatch(es) updated. Stock balance has recomputed.`);
        setShowSwapBranchDialog(false);
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Branch swap failed');
    } finally {
      setSwapLoading(false);
    }
  };

  const handleSaveOCVehicleNumber = async () => {
    const newVeh = editVehicleValue.trim().toUpperCase();
    if (!newVeh) { toast.error('Vehicle number is required'); return; }
    if (newVeh === (oc.vehicle_number || '').toUpperCase()) {
      toast.error('Vehicle number unchanged');
      return;
    }
    try {
      setSavingVehicle(true);
      await orderConfirmationAPI.updateVehicleNumber(oc.id, newVeh);
      toast.success(`Vehicle number updated to ${newVeh}`);
      setShowEditVehicleDialog(false);
      // Trigger parent refresh by calling onBack? No — just optimistically update local + suggest refresh
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vehicle number');
    } finally {
      setSavingVehicle(false);
    }
  };
  const showCrossBranchHint = isSignage && ranchiBranch && oc.branch && oc.branch !== ranchiBranch.id
    && ['Draft', 'Confirmed', 'Ready to Dispatch'].includes(oc.status);

  // Ship-From / Branch mismatch detection — flag when billing_address.name doesn't share any
  // meaningful token with the OC branch name. Compare via token-intersection so partials
  // like "Lucknow Branch" vs "Lucknow Office" still count as a match (token "lucknow" shared).
  const _tokens = (s) => new Set(
    (s || '').toString().toLowerCase().split(/[^a-z0-9]+/).filter(t => t.length >= 3)
  );
  const billingAddrName = oc.billing_address?.name || '';
  const billingTokens = _tokens(billingAddrName);
  const branchTokens = _tokens(ocBranchName);
  const _hasOverlap = billingTokens.size > 0 && branchTokens.size > 0
    && [...billingTokens].some(t => branchTokens.has(t));
  const showBranchMismatchChip = !!(billingAddrName && ocBranchName && branchTokens.size > 0
    && !_hasOverlap
    && !['Cancelled', 'Dispatched'].includes(oc.status));

  const [linkedStns, setLinkedStns] = useState([]);
  const [stnLoading, setStnLoading] = useState(false);
  const [creatingStn, setCreatingStn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!oc?.id) return;
      try {
        setStnLoading(true);
        const res = await orderConfirmationAPI.getLinkedStns(oc.id);
        if (!cancelled) setLinkedStns(res?.stns || []);
      } catch (e) {
        if (!cancelled) setLinkedStns([]);
      } finally {
        if (!cancelled) setStnLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [oc?.id]);

  const handleCreateStnDraft = async () => {
    if (creatingStn) return;
    setCreatingStn(true);
    try {
      const stn = await orderConfirmationAPI.createStnDraft(oc.id);
      toast.success(`STN Draft ${stn.stn_number} created — review & confirm to start the transfer.`);
      // Refresh linked list
      try {
        const res = await orderConfirmationAPI.getLinkedStns(oc.id);
        setLinkedStns(res?.stns || []);
      } catch {}
      // Navigate to STN module with the new STN open
      navigate(`/dashboard/sales?tab=stock-transfer&stn=${stn.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create STN draft');
    } finally {
      setCreatingStn(false);
    }
  };

  const stnStatusColors = {
    Draft: 'bg-gray-100 text-gray-700',
    Confirmed: 'bg-green-100 text-green-700',
    'Ready to Dispatch': 'bg-amber-100 text-amber-700',
    Dispatched: 'bg-blue-100 text-blue-700',
    Received: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div data-testid="oc-detail-view">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{oc.oc_number}</h2>
              {(oc.revision || 0) > 0 && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-purple-100 text-purple-700" data-testid="oc-revision-badge">Rev-{oc.revision}</span>
              )}
            </div>
            <p className="text-[11px] text-gray-500">Order Confirmation {oc.source === 'quotation' ? `(from Quotation)` : '(Direct)'}</p>
          </div>
          <span className={`ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[oc.status] || 'bg-gray-100 text-gray-600'}`}>{oc.status}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Draft actions */}
          {oc.status === 'Draft' && (
            <>
              <button data-testid="oc-edit-draft-btn" onClick={() => onEditDraft(oc)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Edit3 className="w-3.5 h-3.5" /> Edit Draft
              </button>
              <button data-testid="oc-confirm-btn" onClick={() => onConfirmDraft(oc.id)} disabled={confirmLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-green-600 hover:bg-green-700">
                {confirmLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm OC
              </button>
            </>
          )}
          {/* Amend button for Confirmed / Ready to Dispatch */}
          {canAmend && ['Confirmed', 'Ready to Dispatch'].includes(oc.status) && (
            <button data-testid="oc-amend-btn" onClick={() => onAmend(oc)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200">
              <Edit3 className="w-3.5 h-3.5" /> Amend OC
            </button>
          )}
          {canMarkDispatch && ['Confirmed', 'Partially Dispatched'].includes(oc.status) && (
            <button data-testid="oc-ready-dispatch-btn" onClick={() => onDispatch(oc)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-amber-600 hover:bg-amber-700">
              <Truck className="w-3.5 h-3.5" /> {oc.status === 'Partially Dispatched' ? 'Dispatch Remaining' : 'Ready to Dispatch'}
            </button>
          )}
          {canAddInvoice && ['Ready to Dispatch', 'Partially Dispatched'].includes(oc.status) && (
            <button data-testid="oc-invoice-btn" onClick={() => onInvoice(oc)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-[#24549c] hover:bg-[#1a3f7a]">
              <FileText className="w-3.5 h-3.5" /> {oc.tally_invoice_number ? 'Edit Invoice' : 'Add Invoice'}
            </button>
          )}
          {showConfirmTick && (
            <button data-testid="oc-confirm-dispatch-btn" onClick={() => onConfirmDispatch(oc.id)}
              disabled={confirmDispatchLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-emerald-600 hover:bg-emerald-700"
              title="Confirm dispatch — mark as Dispatched">
              {confirmDispatchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4" />}
              Confirm Dispatch
            </button>
          )}
          <button onClick={() => onDownload(oc)} className="p-1.5 rounded-lg hover:bg-gray-100"><Download className="w-3.5 h-3.5 text-gray-500" /></button>
          <button onClick={() => onPrint(oc)} className="p-1.5 rounded-lg hover:bg-gray-100"><Printer className="w-3.5 h-3.5 text-gray-500" /></button>
        </div>
      </div>

      {/* Approval Status */}
      <ApprovalStatusBadge status={oc.status} />
      {/* Sales Document Trail */}
      <SalesDocumentTrail oc={oc} />

      {/* Stuck-dispatch reconciliation banner — fires for Type A (stuck) AND Type B (zero dispatches) */}
      {needsReconcile && (
        <div data-testid="oc-reconcile-banner" className="mt-3 flex items-start gap-3 p-3 rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-rose-50">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-[11px]">
            <p className="font-semibold text-red-900">
              {reconcileKind === 'type_b'
                ? <>Inconsistent state: OC is marked <span className="font-bold">Dispatched</span> but <span className="font-bold">no dispatch records exist</span> — stock has NOT been deducted.</>
                : <>Inconsistent state: OC is marked <span className="font-bold">Dispatched</span>, but {stuckCount} linked dispatch{stuckCount > 1 ? 'es are' : ' is'} still in <span className="font-bold">Awaiting Invoice</span>.</>}
            </p>
            <p className="text-red-700 mt-0.5">
              {reconcileKind === 'type_b'
                ? <>Click <span className="font-bold">Reconcile</span> to create the missing dispatch from OC items (stock deducts automatically) and push a proper status-history entry.</>
                : <>Stock has not been deducted yet. Click <span className="font-bold">Reconcile</span> to activate the dispatch{stuckCount > 1 ? 'es' : ''}, push a proper status-history entry, and {hasInvoice ? 'finalize this OC' : 'upload the missing invoice + finalize this OC'}.</>}
            </p>
          </div>
          <button
            data-testid="oc-reconcile-btn"
            onClick={() => {
              setReconcileInvNumber(oc.tally_invoice_number || '');
              setReconcileInvDate(oc.tally_invoice_date || new Date().toISOString().slice(0,10));
              setReconcileInvUrl(oc.invoice_document_url || '');
              setReconcileInvName(oc.invoice_document_name || '');
              setShowReconcileDialog(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-red-600 hover:bg-red-700">
            <Check className="w-3.5 h-3.5" /> Reconcile
          </button>
        </div>
      )}

      {/* Cross-branch fulfilment hint — Signage produced at Ranchi, OC ships from another branch */}
      {showCrossBranchHint && (
        <div data-testid="oc-cross-branch-hint" className="mt-3 flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <ArrowRightLeft className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-[11px]">
            <p className="font-semibold text-amber-900">
              Signages are produced at <span className="font-bold">{ranchiBranch.name}</span>, but this OC ships from <span className="font-bold">{ocBranchName || 'another branch'}</span>.
            </p>
            <p className="text-amber-700 mt-0.5">
              After production completes, create a Stock Transfer Note from {ranchiBranch.name} → {ocBranchName} so this OC can dispatch successfully.
            </p>
          </div>
          <button
            data-testid="oc-create-stn-draft-btn"
            onClick={handleCreateStnDraft}
            disabled={creatingStn}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg shadow-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50 flex-shrink-0"
          >
            {creatingStn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
            Create STN Draft
          </button>
        </div>
      )}
      {/* OC Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          {/* Customer */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Bill To</span>
            </div>
            <h3 className="text-sm font-bold text-gray-900">{oc.customer?.name}</h3>
            <p className="text-[11px] text-gray-500">{[oc.customer?.address, oc.customer?.city, oc.customer?.state, oc.customer?.pincode].filter(Boolean).join(', ')}</p>
            <div className="flex gap-4 mt-1 text-[10px] text-gray-500">
              {oc.customer?.gstin && <span>GSTIN: {oc.customer.gstin}</span>}
              {oc.customer?.phone && <span>Ph: {oc.customer.phone}</span>}
              {oc.customer?.email && <span>{oc.customer.email}</span>}
            </div>
          </div>

          {/* Billing / Ship From */}
          {oc.billing_address?.name && (
            <div className={`mb-4 p-3 rounded-lg ${showBranchMismatchChip ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Ship From</span>
                {showBranchMismatchChip && (
                  <button
                    type="button"
                    data-testid="oc-shipfrom-mismatch-chip"
                    onClick={() => canAmend && ['Confirmed', 'Ready to Dispatch'].includes(oc.status) ? onAmend(oc) : null}
                    title={`Ship-From "${oc.billing_address.name}" doesn't match Branch "${ocBranchName}". Click to amend the billing address.`}
                    className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200 transition-colors cursor-pointer"
                  >
                    <AlertTriangle className="w-3 h-3" /> Mismatch with Branch
                  </button>
                )}
              </div>
              <p className="text-xs font-semibold text-gray-800">{oc.billing_address.name}</p>
              <p className="text-[10px] text-gray-500">{[oc.billing_address.address, oc.billing_address.city, oc.billing_address.state].filter(Boolean).join(', ')}</p>
              {oc.billing_address.gstin && <p className="text-[10px] text-gray-500">GSTIN: {oc.billing_address.gstin}</p>}
              {showBranchMismatchChip && (
                <p className="mt-1 text-[10px] text-yellow-800">
                  Branch on this OC is <span className="font-semibold">{ocBranchName}</span>, but the printed Ship-From address is <span className="font-semibold">{oc.billing_address.name}</span>. If this is wrong, use <span className="font-semibold">Amend OC → Ship-From Billing Address</span> to fix it before invoicing.
                </p>
              )}
            </div>
          )}

          {/* Ship To (if different from customer) */}
          {oc.ship_to?.name && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-1.5 mb-0.5">
                <MapPin className="w-3 h-3 text-blue-500" />
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Ship To (Delivery Address)</span>
              </div>
              <p className="text-xs font-semibold text-gray-800">{oc.ship_to.name}</p>
              <p className="text-[10px] text-gray-500">{[oc.ship_to.address, oc.ship_to.city, oc.ship_to.state, oc.ship_to.pincode].filter(Boolean).join(', ')}</p>
              <div className="flex gap-4 text-[10px] text-gray-500 mt-0.5">
                {oc.ship_to.contact_person && <span>Contact: {oc.ship_to.contact_person}</span>}
                {oc.ship_to.phone && <span>Ph: {oc.ship_to.phone}</span>}
                {oc.ship_to.gstin && <span>GSTIN: {oc.ship_to.gstin}</span>}
              </div>
            </div>
          )}

          {/* Consignee (if different from customer) */}
          {oc.consignee?.name && (
            <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-1.5 mb-0.5">
                <MapPin className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Consignee (Receiving Authority)</span>
              </div>
              <p className="text-xs font-semibold text-gray-800">{oc.consignee.name}</p>
              <p className="text-[10px] text-gray-500">{[oc.consignee.address, oc.consignee.city, oc.consignee.state, oc.consignee.pincode].filter(Boolean).join(', ')}</p>
              <div className="flex gap-4 text-[10px] text-gray-500 mt-0.5">
                {oc.consignee.contact_person && <span>Contact: {oc.consignee.contact_person}</span>}
                {oc.consignee.phone && <span>Ph: {oc.consignee.phone}</span>}
                {oc.consignee.gstin && <span>GSTIN: {oc.consignee.gstin}</span>}
              </div>
            </div>
          )}

          {/* Items Table */}
          <div className="mb-3">
            <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Items</h4>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-[9px] font-bold text-gray-500 uppercase" style={{ backgroundColor: '#f8f9fb' }}>
                    {isSignage
                      ? ['#', 'Description', 'Shape', 'Dimensions', 'Qty', 'Rate', 'Amount'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)
                      : ['#', 'Product', 'HSN', 'Qty', 'Unit', 'Rate', 'Disc%', 'Amount'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)
                    }
                  </tr>
                </thead>
                <tbody>
                  {isSignage ? (oc.signage_items || []).map((item, idx) => {
                    const effectiveRate = Number(item.custom_per_pcs_rate) || Number(item.per_board_rate) || Number(item.final_per_board) || 0;
                    const siItems = oc.signage_items || [];
                    const _wts = siItems.map(si => si.work_type || '').filter(Boolean);
                    const _mixed = Array.from(new Set(_wts)).length > 1;
                    const rowWT = item.work_type || '';
                    const _sqm = isSqmMode(item) ? sqmLineDisplay(item, item.qty, Number(item.total_amount) || 0) : null;
                    return (
                    <tr key={idx} className="border-t border-gray-100 text-xs align-top">
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium">
                        {item.description || '-'}
                        {_mixed && rowWT && <span className="block text-[10px] text-indigo-600 font-semibold mt-0.5">[{rowWT}]</span>}
                        {item.specific_description && <span className="block text-[10px] text-gray-500 font-normal mt-0.5">Description: {item.specific_description}</span>}
                      </td>
                      <td className="px-3 py-2">{item.shape || '-'}</td>
                      <td className="px-3 py-2 text-gray-500">{item.dimensions || '-'}</td>
                      <td className="px-3 py-2 font-semibold">{_sqm ? `${_sqm.totalSqm} SQM` : item.qty}</td>
                      <td className="px-3 py-2">{_sqm ? _sqm.ratePerSqm.toLocaleString() : effectiveRate.toLocaleString()}</td>
                      <td className="px-3 py-2 font-bold">{(item.total_amount || 0).toLocaleString()}</td>
                    </tr>
                  );}) : (oc.items || []).filter(item => item.product_id || item.product || (item.product_name && item.product_name.trim()) || Number(item.qty) > 0 || Number(item.rate) > 0).map((item, idx) => (
                    <tr key={idx} className="border-t border-gray-100 text-xs">
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <span className="font-medium">{item.product_name}</span>
                        {item.description && (
                          <p className="text-[10px] text-gray-500 mt-0.5">{item.description}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{item.hsn_code || '-'}</td>
                      <td className="px-3 py-2 font-semibold">{item.qty}</td>
                      <td className="px-3 py-2 text-gray-500">{item.unit}</td>
                      <td className="px-3 py-2">{(item.rate || 0).toLocaleString()}</td>
                      <td className="px-3 py-2">{item.discount || 0}%</td>
                      <td className="px-3 py-2 font-bold">{(item.amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-4">
            <div className="w-72 space-y-1">
              {oc.discount_amount > 0 ? (
                <>
                  <div className="flex justify-between text-xs text-gray-600"><span>Net Total</span><span className="font-semibold">INR {(oc.net_total || oc.subtotal || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-xs text-rose-600"><span>Discount{oc.discount_type === 'percent' ? ` (${oc.discount_value}%)` : ''}</span><span className="font-semibold">− INR {(oc.discount_amount || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-xs text-gray-600"><span>Taxable Value</span><span className="font-semibold">INR {(oc.subtotal || 0).toLocaleString()}</span></div>
                </>
              ) : (
                <div className="flex justify-between text-xs text-gray-600"><span>Subtotal</span><span className="font-semibold">INR {(oc.subtotal || 0).toLocaleString()}</span></div>
              )}
              {(oc.other_charges || []).length > 0 && (
                (oc.other_charges || []).map((charge, ci) => (
                  <div key={ci} className="flex justify-between text-xs text-gray-500">
                    <span>{charge.description || 'Other Charge'}{charge.taxable ? ' (+GST)' : ''}</span>
                    <span>INR {(charge.amount || 0).toLocaleString()}</span>
                  </div>
                ))
              )}
              {oc.is_intra_state ? (
                <>
                  <div className="flex justify-between text-xs text-gray-500"><span>CGST ({(oc.gst_rate || 18) / 2}%)</span><span>{(oc.cgst || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-xs text-gray-500"><span>SGST ({(oc.gst_rate || 18) / 2}%)</span><span>{(oc.sgst || 0).toLocaleString()}</span></div>
                </>
              ) : (
                <div className="flex justify-between text-xs text-gray-500"><span>IGST ({oc.gst_rate || 18}%)</span><span>{(oc.igst || oc.gst_amount || 0).toLocaleString()}</span></div>
              )}
              <div className="flex justify-between text-sm font-bold border-t border-gray-200 pt-1"><span>Total</span><span style={{ color: '#24549c' }}>INR {(oc.total || 0).toLocaleString()}</span></div>
            </div>
          </div>

          {/* Terms */}
          {oc.terms && (
            <div className="mb-3">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Terms & Conditions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(oc.terms).map(([key, val]) => val ? (
                  <div key={key} className="text-xs">
                    <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="ml-1 font-medium text-gray-800">{val}</span>
                  </div>
                ) : null)}
              </div>
            </div>
          )}

          {/* Remarks */}
          {oc.remarks && (
            <div className="mb-3 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-1">Remarks</h4>
              <p className="text-xs text-gray-700 whitespace-pre-wrap">{oc.remarks}</p>
            </div>
          )}
        </div>

        {/* Right: BOM (for signage) */}
        <div className="space-y-4">
          {isSignage && oc.bom_items?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Bill of Materials (BOM)</h4>
              <div className="space-y-1">
                {oc.bom_items.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-800">{b.material}</span>
                      {b.description && <span className="text-gray-500 ml-1">— {b.description}</span>}
                    </div>
                    <span className="font-semibold text-gray-700">{b.qty} {b.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-semibold">{oc.oc_type === 'signage' ? 'Signage' : 'Generic'}</span></div>
              <div className="flex justify-between items-center"><span className="text-gray-500">Branch</span>
                <span className="flex items-center gap-1.5">
                  <span className="font-semibold">{oc.branch_name || branches.find(b => b.id === oc.branch)?.name || oc.branch || '-'}</span>
                  {canSwapBranch && (
                    <button
                      data-testid="oc-swap-branch-btn"
                      onClick={() => { setSwapBranchName(''); setSwapPreview(null); setShowSwapBranchDialog(true); }}
                      title="Admin: swap branch on this OC (also updates linked dispatches; stock balance recomputes)"
                      className="text-[10px] font-semibold text-amber-700 hover:text-amber-900 underline">
                      change
                    </button>
                  )}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-gray-500">Salesman</span><span className="font-semibold">{oc.salesman_name || '-'}</span></div>
              {oc.project_name && <div className="flex justify-between"><span className="text-gray-500">Project</span><span className="font-semibold">{oc.project_name}</span></div>}
              {oc.site_project_code && <div className="flex justify-between"><span className="text-gray-500">Project Code</span><span className="font-mono font-semibold text-blue-700">{oc.site_project_code}</span></div>}
              {oc.vehicle_number && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Vehicle</span>
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold text-amber-700" data-testid="oc-vehicle-display">{oc.vehicle_number}</span>
                    {canEditVehicle && (
                      <button
                        type="button"
                        data-testid="oc-edit-vehicle-btn"
                        onClick={() => { setEditVehicleValue(oc.vehicle_number || ''); setShowEditVehicleDialog(true); }}
                        title="Edit vehicle number (available until invoice is uploaded)"
                        className="p-0.5 rounded hover:bg-amber-50 text-amber-600 hover:text-amber-800"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                </div>
              )}
              {oc.latest_dispatch_number && <div className="flex justify-between"><span className="text-gray-500">Dispatch Note</span><span className="font-mono font-semibold text-emerald-700">{oc.latest_dispatch_number}</span></div>}
              {oc.tally_invoice_number && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Invoice</span>
                  {oc.invoice_document_url ? (
                    <a href={`${API_BASE}/api/files/view?path=${encodeURIComponent(oc.invoice_document_url)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="font-semibold text-emerald-700 hover:underline flex items-center gap-1" data-testid="oc-invoice-link">
                      {oc.tally_invoice_number}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="font-semibold text-emerald-700">{oc.tally_invoice_number}</span>
                  )}
                </div>
              )}
              {oc.tally_invoice_date && <div className="flex justify-between"><span className="text-gray-500">Invoice Date</span><span>{oc.tally_invoice_date}</span></div>}
            </div>

            {/* Linked Stock Transfers */}
            {(linkedStns.length > 0 || stnLoading) && (
              <div className="mt-4" data-testid="oc-linked-stns">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
                  <ArrowRightLeft className="w-3 h-3" /> Linked Stock Transfers
                </h4>
                {stnLoading ? (
                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {linkedStns.map(s => (
                      <div
                        key={s.id}
                        data-testid={`oc-linked-stn-${s.stn_number}`}
                        className="p-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-colors"
                        onClick={() => navigate(`/dashboard/sales?tab=stock-transfer&stn=${s.id}`)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-semibold text-[10px] text-gray-800">{s.stn_number}</span>
                            {s.is_auto_linked && <span className="px-1 py-0.5 text-[8px] font-bold rounded bg-purple-100 text-purple-700">AUTO</span>}
                          </div>
                          <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${stnStatusColors[s.status] || 'bg-gray-100 text-gray-600'}`}>{s.status}</span>
                        </div>
                        <div className="flex items-center gap-1 text-[9px] text-gray-600">
                          <span className="truncate">{s.source_branch_name || '?'}</span>
                          <ArrowRightLeft className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{s.destination_branch_name || '?'}</span>
                        </div>
                        <div className="text-[9px] text-gray-500 mt-0.5">
                          {s.item_count} item{s.item_count !== 1 ? 's' : ''} • {s.total_qty} qty
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Status History / Audit Trail */}
            {(oc.status_history || []).length > 0 && (
              <div className="mt-4">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Status History</h4>
                <div className="space-y-2">
                  {(oc.status_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-semibold text-gray-700">{h.from_status || 'Created'}</span>
                          <span className="text-[9px] text-gray-400">&rarr;</span>
                          <span className="text-[10px] font-semibold" style={{ color: '#24549c' }}>{h.to_status}</span>
                        </div>
                        <p className="text-[9px] text-gray-400">{h.changed_by_name} | {h.changed_at ? new Date(h.changed_at).toLocaleString('en-IN') : ''}</p>
                        {h.notes && <p className="text-[9px] text-gray-500 mt-0.5">{h.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amendment History */}
            {amendments.length > 0 && (
              <div className="mt-4" data-testid="oc-amendment-history">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
                  <History className="w-3 h-3" /> Amendment History
                </h4>
                <div className="space-y-2">
                  {amendments.map((a) => (
                    <div key={a.id} className={`p-3 rounded-lg border text-xs ${
                      a.status === 'Approved' ? 'border-green-200 bg-green-50/50' :
                      a.status === 'Rejected' ? 'border-red-200 bg-red-50/50' :
                      'border-amber-200 bg-amber-50/50'
                    }`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${
                            a.status === 'Approved' ? 'bg-green-100 text-green-700' :
                            a.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{a.status}</span>
                          {a.applied_revision && <span className="text-[9px] text-purple-600 font-bold">Rev-{a.applied_revision}</span>}
                        </div>
                        <span className="text-[9px] text-gray-400">{new Date(a.requested_at).toLocaleString('en-IN')}</span>
                      </div>
                      <p className="text-[10px] text-gray-700 mb-1"><strong>Reason:</strong> {a.reason}</p>
                      <p className="text-[9px] text-gray-500">By {a.requested_by_name}</p>
                      {a.changes_summary?.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {a.changes_summary.map((c, ci) => (
                            <div key={ci} className="text-[9px] text-gray-600">
                              <span className="font-semibold">{c.item}:</span> {c.changes?.join(', ')}
                            </div>
                          ))}
                        </div>
                      )}
                      {a.detail_changes?.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {a.detail_changes.map((d, di) => (
                            <div key={di} className="text-[9px] text-gray-600">
                              <span className="font-semibold capitalize">{d.field.replace(/_/g, ' ')}:</span>{' '}
                              <span className="text-red-600 line-through">{d.old}</span>{' '}
                              <span className="text-green-700 font-semibold">→ {d.new}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-3 text-[9px] text-gray-500">
                        <span>Original: INR {(a.original_total || 0).toLocaleString()}</span>
                        <span>&rarr;</span>
                        <span className="font-semibold">Proposed: INR {(a.proposed_total || 0).toLocaleString()}</span>
                      </div>
                      {a.review_notes && <p className="mt-1 text-[9px] text-gray-500 italic">Review: {a.review_notes}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Centralized Revision History */}
            <RevisionHistory docType="order_confirmation" docId={oc.id} />
          </div>
        </div>
      </div>

      {/* Edit Vehicle Number Dialog (Awaiting Invoice + no invoice only) */}
      {showEditVehicleDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="oc-edit-vehicle-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Truck className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Edit Vehicle Number</h3>
            </div>
            <p className="text-[11px] text-gray-600 mb-3">
              Change the vehicle number for <span className="font-semibold">{oc.oc_number}</span> (dispatch <span className="font-semibold">{oc.latest_dispatch_number || 'pending'}</span>).
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
              <p className="text-[10px] text-blue-900">
                💡 Editable only while the linked dispatch is Awaiting Invoice. Once the invoice is uploaded, the vehicle number is locked so the printed Tally invoice and ERP record always match.
              </p>
            </div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">New Vehicle Number <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={editVehicleValue}
              onChange={e => setEditVehicleValue(e.target.value.toUpperCase())}
              placeholder="e.g. JH01XY9999"
              data-testid="oc-edit-vehicle-input"
              maxLength={20}
              className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg uppercase font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <p className="text-[10px] text-gray-500 mt-1">Current: <span className="font-mono">{oc.vehicle_number || '-'}</span></p>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => setShowEditVehicleDialog(false)}
                disabled={savingVehicle}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="oc-edit-vehicle-save-btn"
                onClick={handleSaveOCVehicleNumber}
                disabled={savingVehicle || !editVehicleValue.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {savingVehicle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Update Vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconcile-Dispatch Dialog — appears when banner button is clicked */}
      {showReconcileDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" data-testid="oc-reconcile-dialog">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" /> Reconcile Stuck Dispatch — {oc.oc_number}
            </h3>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-[11px] text-red-800">
                {stuckCount} dispatch{stuckCount > 1 ? 'es' : ''} stuck in <span className="font-bold">Awaiting Invoice</span>.
                Stock has NOT been deducted yet. Reconciling will:
              </p>
              <ul className="text-[11px] text-red-700 mt-1.5 ml-4 list-disc">
                <li>Activate the dispatch{stuckCount > 1 ? 'es' : ''} → stock deduction</li>
                <li>{hasInvoice ? 'Use the existing invoice details' : 'Save the invoice details below'}</li>
                <li>Push a "Ready to Dispatch → Dispatched" entry to status history</li>
              </ul>
            </div>
            {!hasInvoice && (
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Tally Invoice Number <span className="text-red-500">*</span></label>
                  <input type="text" value={reconcileInvNumber} onChange={e => setReconcileInvNumber(e.target.value)}
                    placeholder="e.g. INV-2026-0001"
                    data-testid="reconcile-invoice-number"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Invoice Date</label>
                  <input type="date" value={reconcileInvDate} onChange={e => setReconcileInvDate(e.target.value)}
                    data-testid="reconcile-invoice-date"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Upload Invoice Document <span className="text-red-500">*</span></label>
                  {reconcileInvUrl ? (
                    <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <FileText className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-[11px] font-medium text-gray-700 truncate flex-1">{reconcileInvName || 'Invoice'}</span>
                      <button onClick={() => { setReconcileInvUrl(''); setReconcileInvName(''); }} className="p-0.5 rounded hover:bg-emerald-100" data-testid="reconcile-clear-doc">
                        <span className="text-xs text-gray-500">✕</span>
                      </button>
                    </div>
                  ) : (
                    <label className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] border border-dashed border-gray-300 rounded-lg hover:border-red-400 text-gray-500 cursor-pointer">
                      <FileText className="w-3.5 h-3.5" /> Click to upload invoice PDF / image
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        data-testid="reconcile-upload-file"
                        onChange={async (e) => {
                          const file = e.target.files[0]; if (!file) return;
                          try {
                            const { fileAPI } = await import('../../services/api');
                            const result = await fileAPI.uploadDocument(file);
                            setReconcileInvUrl(result.file_path);
                            setReconcileInvName(file.name);
                          } catch (err) {
                            toast.error('Upload failed');
                          }
                          e.target.value = '';
                        }} />
                    </label>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowReconcileDialog(false)} disabled={reconcileLoading}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleReconcile} disabled={reconcileLoading || (!hasInvoice && (!reconcileInvNumber.trim() || !reconcileInvUrl))}
                data-testid="oc-reconcile-confirm-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {reconcileLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Confirm Reconcile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ ADMIN: Branch swap dialog (one-off for OC-2026-0404 + similar) ============ */}
      {showSwapBranchDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="oc-swap-branch-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Swap OC Branch (admin)</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">Updates OC header branch + every linked dispatch&rsquo;s from_branch. Stock balance recomputes automatically.</p>
              </div>
              <button onClick={() => setShowSwapBranchDialog(false)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-[11px] text-gray-500">
                <div className="flex justify-between"><span>Current branch:</span><span className="font-mono font-semibold text-gray-900">{ocBranchName || oc.branch || '—'}</span></div>
                <div className="flex justify-between mt-0.5"><span>OC status:</span><span className="font-semibold text-gray-900">{oc.status}</span></div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">New branch name</label>
                <input
                  data-testid="oc-swap-branch-input"
                  type="text"
                  value={swapBranchName}
                  onChange={e => setSwapBranchName(e.target.value)}
                  placeholder="e.g. Bhubaneswar Godown"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
              </div>
              <label className="flex items-center gap-2 text-[11px] text-gray-700">
                <input type="checkbox" checked={swapMirrorShip} onChange={e => setSwapMirrorShip(e.target.checked)} />
                Also update <code className="text-gray-500">ship_from</code> to match new branch address
              </label>
              {swapPreview && (
                <div className="text-[10px] bg-gray-50 border border-gray-100 rounded-lg p-2 font-mono text-gray-700 space-y-1">
                  <div>Old: {swapPreview.old_branch_name}</div>
                  <div>New: {swapPreview.new_branch_name}</div>
                  <div>Linked dispatches to update: <strong>{swapPreview.dispatches_to_update?.length || 0}</strong></div>
                  {(swapPreview.dispatches_to_update || []).map(d => (
                    <div key={d.dispatch_number} className="pl-2">• {d.dispatch_number} ({d.status})</div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => runSwapBranch(false)} disabled={swapLoading || !swapBranchName.trim()}
                data-testid="oc-swap-branch-dryrun-btn"
                className="px-3 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {swapLoading ? 'Working…' : 'Preview (dry-run)'}
              </button>
              <button
                onClick={() => runSwapBranch(true)} disabled={swapLoading || !swapPreview}
                data-testid="oc-swap-branch-apply-btn"
                title={!swapPreview ? 'Run a preview first' : 'Apply the swap (irreversible — but a status_history audit row is recorded)'}
                className="px-3 py-1.5 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50">
                {swapLoading ? 'Working…' : 'Apply Swap'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCDetailView;
